/**
 * SharePoint-RxDB Data Mappers (JSON-only, stateless)
 */

function _identity(object, field) {
  return object[field] ?? undefined;
}

function _identityWithDefault(defaultValue) {
  return (object, field) => object[field] ?? defaultValue;
}

function _safeDateTime(object, field) {
  const value = object[field];
  if (!value) return undefined;
  return new Date(value).toISOString();
}

function _safeGUID(object, field) {
  const value = object[field];
  if (!value) return undefined;
  return value.replace(/[{}]/g, '').toLowerCase();
}

function _safeNumber(object, field) {
  const value = object[field];
  if (value === null || value === undefined) return undefined;
  const num = Number(value);
  return isNaN(num) ? undefined : num;
}

function _safeBoolean(object, field) {
  const value = object[field];
  if (value === null || value === undefined) return undefined;
  return Boolean(value);
}

function _jsonSerializer(object, field) {
  return JSON.stringify(object[field] ?? null);
}

function _jsonDeserializer(object, field) {
  const value = object[field];
  if (value === undefined) return undefined;
  try {
    return JSON.parse(value) ?? undefined;
  } catch (e) {
    console.warn('Failed to parse JSON field:', field, value);
    return undefined;
  }
}

/**
 * Wraps a transformer to handle nested path navigation
 * @param {string[]} parts - Precomputed path parts
 * @param {Function} transformer - The transformer to apply to the final value
 */
function _unNest(parts, transformer) {
  return spItem => {
    let value = spItem;
    for (let i = 0; i < parts.length - 1; i++) {
      value = value?.[parts[i]];
      if (value === null || value === undefined) return undefined;
    }
    return transformer(value, parts[parts.length - 1]);
  };
}

/**
 * Creates mapping functions for converting between SharePoint and RxDB data structures.
 * @param options
 */
export function createMappingFunctions(options) {
  const {
    primaryPath = 'id',
    fieldMapping = {},
    rxdbSchema,
    listItemEntityTypeFullName,
  } = options;

  if (!rxdbSchema || !listItemEntityTypeFullName) {
    throw new Error(
      'createMappingFunctions: rxdbSchema and listItemEntityTypeFullName are required'
    );
  }

  const fields = Object.keys(rxdbSchema.properties).filter(
    x => !x.startsWith('_') || x === '_deleted'
  );
  const fullFieldMapping = {};
  const reverseFieldMapping = {};
  const rxToSpFields = {};
  const spToRxFields = {};
  const requiredFields = rxdbSchema.required ?? [];
  const selectFieldsSet = new Set(['Id', 'Modified']);
  const expandEntitiesSet = new Set();

  for (const field of fields) {
    const isRequired = requiredFields.includes(field);
    const spField = fieldMapping[field] ?? field;
    const rxProperty = rxdbSchema.properties[field];
    fullFieldMapping[field] = spField;
    reverseFieldMapping[spField] = field;
    selectFieldsSet.add(spField);

    switch (rxProperty.type) {
      case 'string':
        rxToSpFields[field] = _identity;
        if (rxProperty.format === 'date-time') {
          spToRxFields[spField] = _safeDateTime;
        } else if (rxProperty.format === 'uuid') {
          spToRxFields[spField] = _safeGUID;
        } else {
          spToRxFields[spField] = _identityWithDefault('');
        }
        break;
      case 'number':
      case 'integer':
        rxToSpFields[field] = _identity;
        spToRxFields[spField] = _safeNumber;
        break;
      case 'boolean':
        rxToSpFields[field] = _identity;
        spToRxFields[spField] = isRequired
          ? _identityWithDefault(false)
          : _identity;
        break;
      case 'array':
      case 'object':
        rxToSpFields[field] = _jsonSerializer;
        spToRxFields[spField] = _jsonDeserializer;
        break;
    }

    if (spField.includes('/')) {
      const parts = spField.split('/');
      for (let i = 0; i < parts.length - 1; i++) {
        expandEntitiesSet.add(parts[i]);
      }
      // Nested fields are read-only (can't push back to SharePoint lookups)
      delete rxToSpFields[field];
      spToRxFields[spField] = _unNest(parts, spToRxFields[spField]);
    }
  }

  const mapToRxDoc = spItem => {
    if (!spItem) return null;
    return Object.fromEntries(
      Object.entries(spToRxFields)
        .map(([field, transformer]) => [
          reverseFieldMapping[field],
          transformer(spItem, field),
        ])
        .filter(([_, value]) => value !== undefined)
    );
  };

  const mapToSpItem = rxDoc => {
    if (!rxDoc) return null;
    const spItem = Object.fromEntries(
      Object.entries(rxToSpFields).map(([field, transformer]) => [
        fullFieldMapping[field],
        transformer(rxDoc, field),
      ])
    );
    spItem.__metadata = { type: listItemEntityTypeFullName };
    return spItem;
  };

  // __metadata is always selected and expanded by default
  expandEntitiesSet.delete('__metadata');
  const selectFields = Array.from(selectFieldsSet).filter(
    field => !field.startsWith('__metadata/')
  );
  const expandEntities = Array.from(expandEntitiesSet);

  return {
    mapToRxDoc,
    mapToSpItem,
    fieldMapping: fullFieldMapping,
    selectFields,
    expandEntities,
  };
}
